import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import { 
  Lightning, 
  Drop, 
  Plugs, 
  CurrencyCircleDollar, 
  HouseLine, 
  ChartLineUp, 
  FileText, 
  Scales,
  ArrowCounterClockwise,
  Leaf,
  CheckCircle,
  ArrowRight,
  Clock,
  Target,
  User
} from '@phosphor-icons/react';

const SERVICE_TYPES = [
  { id: 'Water Query', name: 'Water Query', icon: Drop, image: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/g5symzd8_Water%20Query.jpeg', description: 'Resolve water billing disputes and meter issues' },
  { id: 'Electricity Query', name: 'Electricity Query', icon: Lightning, image: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/b83hngwd_Electricty%20Query.jpg', description: 'Address electricity billing and consumption queries' },
  { id: 'New Connection', name: 'New Connection', icon: Plugs, image: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/qznbo43g_New%20Connection.avif', description: 'Apply for new water or electricity connections' },
  { id: 'New Account Opening', name: 'New Account Opening', icon: User, image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&w=940&h=650&q=80', description: 'Open a new municipal services account in your name' },
  { id: 'Payment Arrangement', name: 'Payment Arrangement', icon: CurrencyCircleDollar, image: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/cpebri9l_Payment%20Arrangement%202.jpg', description: 'Negotiate payment plans with municipalities' },
  { id: 'Building Plans', name: 'Building Plans', icon: HouseLine, image: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/od4xm98y_Building%20Plans%202.webp', description: 'Submit and track building plan approvals' },
  { id: 'Tariff Change Application', name: 'Tariff Change Application', icon: ChartLineUp, image: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/io0znaxh_Tariff%20Change.jpeg', description: 'Apply for optimal tariff structures' },
  { id: 'Billing Query', name: 'Billing Query', icon: FileText, image: 'https://images.pexels.com/photos/7651555/pexels-photo-7651555.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', description: 'Investigate and resolve billing discrepancies' },
  { id: 'Value Dispute', name: 'Value Dispute', icon: Scales, image: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/tf182wfw_Value%20Dispute.jpeg', description: 'Challenge and resolve property or service value disputes' },
  { id: 'Refund Request', name: 'Refund Request', icon: ArrowCounterClockwise, image: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/ocopfq97_Refund.jpeg', description: 'Process and track municipal refund applications' },
  { id: 'Energy Audit', name: 'Energy Audit', icon: Leaf, image: 'https://images.pexels.com/photos/8293678/pexels-photo-8293678.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', description: 'Comprehensive energy usage analysis and optimisation' },
];

const VALUE_PROPS = [
  { title: 'SAVE', subtitle: 'Money', icon: Target },
  { title: 'SAVE', subtitle: 'Environment', icon: Clock },
  { title: 'SAVE', subtitle: 'Electricity', icon: Lightning },
  { title: 'LIVE', subtitle: 'Better', icon: User },
];

const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <PublicHeader transparent />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')`,
          }}
        >
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="max-w-3xl">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-light text-white leading-tight mb-8">
              Energy Management<br />
              <span className="font-normal">Services</span>
            </h1>
            
            <div className="bg-[#1a2332]/90 p-6 mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white uppercase tracking-wide">
                Are you being overcharged<br />on your electricity bill?
              </h2>
            </div>
            
            <div className="mb-6">
              <Link to="/contact">
                <Button className="bg-[#4CAF50] hover:bg-[#45a049] text-white border-2 border-[#4CAF50] rounded-none px-8 py-3 text-lg font-medium" data-testid="ask-us-how-btn">
                  Ask Us How!
                </Button>
              </Link>
            </div>
            <p className="text-lg text-gray-200 max-w-xl">
              We can help you save between 10-50%. That is cash that you can use to help grow your company and improve your bottom line.
            </p>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {VALUE_PROPS.map((prop, index) => (
              <div key={index} className="flex flex-col items-center">
                <prop.icon className="w-12 h-12 text-gray-600 mb-4" weight="light" />
                <p className="text-2xl font-bold text-gray-800">{prop.title}</p>
                <p className="text-gray-500">{prop.subtitle}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-[#4CAF50]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl sm:text-5xl font-light text-white mb-6">
            About Marema Consulting
          </h2>
          <p className="text-xl text-white/90 font-medium mb-8">Established in 2006</p>
          <p className="text-lg text-white/80 leading-relaxed max-w-3xl mx-auto">
            We are focused on intelligent and transparent energy management services for businesses and organizations. Our approach centers on monitoring, controlling, and optimising energy usage within buildings and specific sites to drive efficiency and sustainability.
          </p>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-light text-gray-800 mb-4">
              Our Services
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive municipal account management services to help your business save money and operate efficiently.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICE_TYPES.map((service) => (
              <div 
                key={service.id}
                className="bg-white p-8 shadow-sm hover:shadow-lg transition-shadow group"
                data-testid={`service-card-${service.id.toLowerCase().replace(/ /g, '-')}`}
              >
                {service.image ? (
                  <div className="w-20 h-20 rounded-full overflow-hidden mb-6 border-2 border-[#4CAF50]/20 group-hover:border-[#4CAF50] transition-colors">
                    <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-20 h-20 bg-[#4CAF50]/10 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#4CAF50] transition-colors">
                    <service.icon className="w-9 h-9 text-[#4CAF50] group-hover:text-white transition-colors" weight="light" />
                  </div>
                )}
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{service.name}</h3>
                <p className="text-sm text-gray-500">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <h2 className="text-4xl sm:text-5xl font-light text-gray-800 mb-6">
              Why Choose Us?
            </h2>
            <p className="text-gray-600 mb-8 leading-relaxed">
              With over 18 years of experience in energy management, we have the expertise to help your business navigate the complexities of municipal accounts and Eskom relationships.
            </p>
            <ul className="space-y-4">
              {[
                'On-site consultations at your premises',
                'Document preparation and submission',
                'Real-time progress tracking and updates',
                'POPIA compliant data handling',
                'Dedicated account management'
              ].map((item, index) => (
                <li key={index} className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-[#4CAF50]" weight="fill" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/portal/login" className="inline-block mt-8">
              <Button className="bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none px-8 py-3 font-medium" data-testid="client-portal-btn">
                Access Client Portal <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Our Clients */}
      <section className="py-24 bg-gray-50" data-testid="our-clients-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-light text-gray-800 mb-4">
              Our Clients
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Trusted by leading organizations across South Africa.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-12 md:gap-16">
            {[
              { name: 'Kaizer Chiefs', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/c2wm21ds_Kaizer_Chiefs_logo.svg.png' },
              { name: 'Cummins', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/ibs44fik_Cummins%20logo.png' },
              { name: 'HB Realty', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/n5n32zih_HB%20Realty%20logo.png' },
              { name: 'Investec', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/u1m1n2is_Investec%20Logo.jpg' },
              { name: 'Italtile', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/m003l1z6_Italtile%20Logo.png' },
              { name: 'Easylife Kitchens', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/c1ekvsze_ELK%20Logo%201.png' },
              { name: 'Excellerate JHI', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/6szqumho_Excellerate%20JHI%20Logo.jpg' },
              { name: 'Africrest Properties', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/90cfdowp_Africrest%20Properties%20Logo.jpg' },
              { name: 'CTM', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/hynhpxrs_CTM%20Logo.jpg' },
              { name: 'U-Light', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/tefpxx0j_U-Light%20Logo.png' },
              { name: 'Engen', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/qg6v8cog_Engen%20Logo.png' },
              { name: 'PSL', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/p13uhda4_PSL%20Logo.jpg' },
              { name: 'Plastics SA', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/egg8pg7y_Plastics%20SA%20Logo.png' },
              { name: 'Earlybird Property', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/h6ysyefa_Earlybird%20Propert%20Logo.jpg' },
              { name: 'Sappi', src: 'https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/25h7rze0_Sappi%20logo.png' },
            ].map((client) => (
              <div key={client.name} className="flex items-center justify-center" data-testid={`client-logo-${client.name.toLowerCase().replace(/ /g, '-')}`}>
                <img
                  src={client.src}
                  alt={client.name}
                  className="h-20 md:h-24 w-auto object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default LandingPage;
